<template>
  <div class="bg-background text-foreground min-h-screen">
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>

    <!-- Modals and Overlays - These can often stay outside the main layout if they are true global overlays -->
    <ClientOnly>
      <!-- Global modals and overlays -->
      <Suspense>
        <GlobalModals />
        <template #fallback>
          <div class="fixed inset-0 bg-background/80 backdrop-blur-xs" />
        </template>
      </Suspense>
    </ClientOnly>

    <!-- Toast Notifications -->
    <ClientOnly>
      <ToastContainer />
    </ClientOnly>
    
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useAgentStore } from '~/stores/agent'
import { useSiteStore } from '~/stores/site'

import { useRouter } from '#imports'
import { useSafeHead } from '~/app/composables/useSafeHead'

// Initialize color mode
const colorMode = useColorMode()

// Set up color mode meta tag reactively
const themeColor = computed(() => colorMode.value === 'dark' ? '#0f172a' : '#ffffff')

useSafeHead({
  meta: [
    {
      name: 'theme-color',
      content: themeColor
    }
  ],
  htmlAttrs: {
    lang: 'en'
  }
})

// State
const isTransitioning = ref(false)
const isInitialized = ref(false)

// Store initialization - moved to onMounted to avoid "no active Pinia" error

// Ensure HTML has correct theme attributes and enable transitions after load
const applyThemeAttributes = () => {
  if (process.client) {
    const root = document.documentElement
    const theme = colorMode.value
    root.classList.toggle('dark', theme === 'dark')
    root.setAttribute('data-theme', theme)
  }
}

// Declare stores at module level to avoid TDZ issues
let agentStore: any = null
let siteStore: any = null

watch(() => colorMode.value, () => {
  try {
    applyThemeAttributes()
  } catch (error) {
    console.error('[App] Failed to apply theme attributes:', error)
  }
})

const router = useRouter()

// Store initialization moved to main onMounted function below

// Transition handlers
const handleBeforeEnter = () => {
  nextTick(() => {
    isTransitioning.value = true
  })
}

const handleAfterEnter = () => {
  nextTick(() => {
    isTransitioning.value = false
  })
}

const handleBeforeLeave = () => {
  if (process.client) {
    document.body.classList.add('page-transitioning-leave');
  }
};

const handleAfterLeave = () => {
  if (process.client) {
    document.body.classList.remove('page-transitioning-leave');
  }
};

// SEO configuration - will be set up after store initialization
// Default head configuration
useSafeHead({
  titleTemplate: (title) => {
    return title || 'Bolts V5'
  },
  meta: [
    { name: 'description', content: 'Modern automotive website platform' },
    { name: 'theme-color', content: '#ffffff' },
    { name: 'viewport', content: 'width=device-width, initial-scale=1, maximum-scale=1' },
    { name: 'format-detection', content: 'telephone=no' }
  ],
  link: [
    { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' },
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' }
  ]
})

// Initialize data with performance optimizations
onMounted(async () => {
  try {
    // Initialize stores after Pinia is ready
    await nextTick()
    
    // Initialize stores
    agentStore = useAgentStore()
    siteStore = useSiteStore()
    
    // Initialize store data (site config is already hydrated server-side)
    await agentStore.fetchInitialData()
    await siteStore.initialize() // Just checks if data exists
    
    // Update SEO with store data
    if (siteStore.name || siteStore.description) {
      useSafeHead({
        titleTemplate: (title) => {
          return title ? `${title} - ${siteStore.name}` : siteStore.name || 'Bolts V5'
        },
        meta: [
          { name: 'description', content: siteStore.description || 'Modern automotive website platform' }
        ]
      })
    }
    
    isInitialized.value = true
    
    // Enable smooth scrolling after critical content is loaded
    nextTick(() => {
      document.documentElement.classList.add('loaded')
    })
  } catch (error) {
    console.error('[App] Error initializing data:', error)
  }
})

// Cleanup on unmount
onBeforeUnmount(() => {
  isInitialized.value = false
})

// Add hydration event handlers
const onComponentHydrated = (componentName: string) => {
  // Remove console.log(`[App] Component ${componentName} hydrated`)
}

// Register hydration event listener
onMounted(() => {
  window.addEventListener('component-hydrated', ((event: Event) => {
    const customEvent = event as CustomEvent<{ componentName: string }>
    onComponentHydrated(customEvent.detail.componentName)
  }) as EventListener)
})

onBeforeUnmount(() => {
  window.removeEventListener('component-hydrated', ((event: Event) => {
    const customEvent = event as CustomEvent<{ componentName: string }>
    onComponentHydrated(customEvent.detail.componentName)
  }) as EventListener)
})

// Track active view transition
let activeTransition: any = null

/* // Temporarily disable View Transitions API to isolate issues
onMounted(() => {
  if (!document.startViewTransition) return
  
  router.afterEach(() => {
    if (activeTransition) return
    
    try {
      activeTransition = document.startViewTransition(() => {
        return new Promise<void>(resolve => {
          nextTick(() => {
            resolve()
          })
        })
      })
      
      activeTransition.finished.finally(() => {
        activeTransition = null
      })
    } catch (error) {
      console.error('[View Transition] Error:', error)
      activeTransition = null
    }
  })
})
*/
</script>

<style>
/* Page Transitions */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.3s ease; /* Keep duration for the new page to fade in */
}

.page-enter-from {
  opacity: 0;
}

/* Make outgoing page immediately transparent, but let it occupy space for transition duration */
.page-leave-from { 
  opacity: 1; /* Start fully visible */
  visibility: visible;
}
.page-leave-active { /* During the leave transition */
  opacity: 0; /* Make it transparent quickly */
  visibility: hidden;
  transition: opacity 0.05s ease, visibility 0s linear 0.05s; /* Transition opacity, then snap visibility */
}
.page-leave-to {
  opacity: 0; /* Ensure it ends up transparent */
  visibility: hidden;
}

/* Layout Transitions */
.layout-enter-active,
.layout-leave-active {
  transition: opacity 0.3s ease;
}

.layout-enter-from,
.layout-leave-to {
  opacity: 0;
}

/* Class to prevent scrolling during page transitions */
.page-transitioning-leave {
  overflow-y: hidden !important; /* !important to ensure override */
}

/* Loading States */
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: .5;
  }
}

/* Optimize paint operations */
* {
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Optimize scrolling - only enable after page load to prevent FOUC interference */
html.loaded {
  scroll-behavior: smooth;
}

/* Optimize images */
img {
  content-visibility: auto;
  contain: paint;
}
</style>